import React from 'react';
import { Icon } from '@repo/ui';
import { Button } from '@repo/ui';
import type { WebhookSubscription } from '../types';

interface WebhookSubscriptionCardProps {
  webhook: WebhookSubscription;
  onRecreate: (modelName: string) => void;
  onDelete: (modelName: string) => void;
  isRecreating: boolean;
  isDeleting: boolean;
}

export const WebhookSubscriptionCard: React.FC<WebhookSubscriptionCardProps> = ({
  webhook,
  onRecreate,
  onDelete,
  isRecreating,
  isDeleting,
}) => {
  const getStatusBadge = () => {
    const isActive = webhook.enabled && webhook.clioWebhookId;
    const hasAutoProcessing = webhook.autoProcessingEnabled;

    if (isActive && hasAutoProcessing) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-xtnd-blue text-xtnd-white">
          Active + Auto-Processing
        </span>
      );
    } else if (isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Active (No Auto-Processing)
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-xtnd-dark-50 text-xtnd-dark-700">
          Disabled
        </span>
      );
    }
  };

  return (
    <div className="border border-xtnd-light rounded-lg p-4">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white capitalize">
              {webhook.modelName}
            </h3>
            {getStatusBadge()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-xtnd-dark dark:text-xtnd-white">Events:</span>
              <span className="ml-2 text-xtnd-dark-500 dark:text-xtnd-dark-300">
                {webhook.events && webhook.events.length > 0
                  ? webhook.events.join(', ')
                  : 'No events configured'}
              </span>
            </div>

            {webhook.clioWebhookId && (
              <div>
                <span className="font-medium text-xtnd-dark dark:text-xtnd-white">Webhook ID:</span>
                <span className="ml-2 text-xtnd-dark-500 dark:text-xtnd-dark-300 font-mono">
                  {webhook.clioWebhookId}
                </span>
              </div>
            )}

            <div>
              <span className="font-medium text-xtnd-dark dark:text-xtnd-white">Status:</span>
              <span className="ml-2 text-xtnd-dark-500 dark:text-xtnd-dark-300">{webhook.status}</span>
            </div>

            {webhook.updatedAt && (
              <div>
                <span className="font-medium text-xtnd-dark dark:text-xtnd-white">Updated:</span>
                <span className="ml-2 text-xtnd-dark-500 dark:text-xtnd-dark-300">
                  {new Date(webhook.updatedAt).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex space-x-2 ml-4">
          <Button
            onClick={() => onRecreate(webhook.modelName)}
            disabled={isRecreating}
            variant="outline"
            size="sm"
            leftIcon={<Icon name="HiRefresh" size="sm" />}
          >
            Recreate
          </Button>
          <Button
            onClick={() => onDelete(webhook.modelName)}
            disabled={isDeleting}
            variant="outline"
            size="sm"
            leftIcon={<Icon name="HiTrash" size="sm" />}
            className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

